import { type LoaderActivity } from '@littlebird/ui-utils';
import { motion, useTransform } from 'motion/react';

import { AppLogo } from '@/assets/icons/AppLogo';

import { type LogoMotionValues } from './logo-acts';
import { useLogoLoader, type LogoLoaderHandle } from './use-logo-loader';

const PERCH_BRANCH_PATH = [
  'M3.8 16.9C7 16.6 9.5 17.1 12.5 16.7C15.5 16.3 18 16.4 21 15.9C22.5 15.7 23.6 15.5 24.5 15.4L24.5 15.8C23.6 15.9 22.5 16.2 21 16.5C18 17.1 15.5 17.2 12.5 17.7C9.5 18.2 7 17.8 3.8 18.1Z',
  'M20.2 16.2C20.9 15.5 21.6 15 22.7 14.5L22.9 14.8C21.9 15.3 21.3 15.9 20.8 16.5Z',
  'M5.2 16.9C4.8 16.4 4.4 16 3.9 15.6L3.6 15.9C4.1 16.3 4.5 16.7 4.8 17.2Z',
].join(' ');
const PERCH_BRANCH_OPACITY = 0.35;
const BIRD_WIDTH = '100%';

export const LogoBirdStage = ({ values }: { values: LogoMotionValues }) => {
  const branchOpacity = useTransform(
    values.perch,
    (perch) => perch * PERCH_BRANCH_OPACITY
  );

  return (
    <div className="relative size-full">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-start pl-1">
        <motion.svg
          width={BIRD_WIDTH}
          viewBox="0 0 25 20"
          overflow="visible"
          aria-hidden
          style={{ opacity: branchOpacity }}
        >
          <path d={PERCH_BRANCH_PATH} fill="currentColor" />
        </motion.svg>
      </div>
      <motion.div
        className="flex size-full items-center justify-start pl-1"
        style={{
          x: values.x,
          y: values.y,
          rotate: values.rotate,
          scaleX: values.scaleX,
          scaleY: values.scaleY,
          opacity: values.opacity,
        }}
      >
        <AppLogo
          size={BIRD_WIDTH}
          customColor="currentColor"
          wingAngle={values.wingAngle}
          wingFold={values.wingFold}
          eyeGaze={values.eyeGaze}
          decorative
        />
      </motion.div>
    </div>
  );
};

export const LogoLoaderView = ({
  values,
  containerRef,
  poke,
}: LogoLoaderHandle) => (
  <div
    ref={containerRef}
    role="status"
    aria-label="Thinking"
    className="pointer-events-auto size-full"
    onPointerDown={poke}
  >
    <LogoBirdStage values={values} />
  </div>
);

type LogoLoaderProps = {
  activity?: LoaderActivity;
  flightOnly?: boolean;
};

export const LogoLoader = (props: LogoLoaderProps) => (
  <LogoLoaderView {...useLogoLoader(props)} />
);
